// src/features/services/stock/components/StockDashboard.tsx
// ─────────────────────────────────────────────────────────────────────────────
// P3 — Stock Management Dashboard (zero-prop, hook-owns-all-state).
//
// Tabs:
//   Balances   — BalanceGrid with per-SKU drill-down
//   Alerts     — Low-stock alerts (P3.1/P3.2)
//   Movements  — Movement timeline for selected SKU (P3.1)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import Tabs from '@/shared/components/ui/Tabs';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useStock, useLowStockAlerts } from '@/features/services/sales/hooks/useStock';
import { fetchMovementsApi, type StockMovement } from '@/features/services/sales/api/stockApi';
import BalanceGrid from './BalanceGrid';
import LowStockAlerts from './LowStockAlerts';
import MovementTimeline from './MovementTimeline';

const TAB_LABELS = ['Balances', 'Alerts', 'Movements'];

export default function StockDashboard() {
  const [activeTab, setActiveTab] = useState('Balances');
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  useDocumentTitle('Stock', activeTab);

  const stock = useStock();
  const alerts = useLowStockAlerts();

  const handleSelectSku = useCallback(async (sku: string) => {
    setSelectedSku(sku);
    setActiveTab('Movements');
    setMovementsLoading(true);
    try {
      const res = await fetchMovementsApi(sku, { limit: 200 });
      setMovements(res.items ?? []);
    } catch {
      setMovements([]);
    } finally {
      setMovementsLoading(false);
    }
  }, []);

  // ── Stat summary ──────────────────────────────────────────────────
  const totalSkus = stock.data?.items?.length ?? 0;
  const alertCount = alerts.data?.items?.length ?? 0;

  return (
    <section className="space-y-6">
      {/* Tabs */}
      <Tabs tabs={TAB_LABELS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-neutral-100">Stock Management</h1>
        <div className="flex gap-3">
          <StatBadge label="Total SKUs" value={totalSkus} />
          <StatBadge label="Low Stock" value={alertCount} accent={alertCount > 0 ? 'text-red-400' : undefined} />
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'Balances' && (
        <BalanceGrid
          items={stock.data?.items ?? []}
          isLoading={stock.isLoading}
          onSelectSku={handleSelectSku}
        />
      )}

      {activeTab === 'Alerts' && (
        <LowStockAlerts
          items={alerts.data?.items ?? []}
          isLoading={alerts.isLoading}
        />
      )}

      {activeTab === 'Movements' && (
        <div className="space-y-3">
          {selectedSku ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Movements for</span>
                <span className="font-semibold text-white">{selectedSku.toUpperCase()}</span>
                <button
                  onClick={() => { setSelectedSku(null); setMovements([]); }}
                  className="text-xs text-gray-500 hover:text-gray-300 ml-2"
                >
                  ✕ clear
                </button>
              </div>
              <MovementTimeline items={movements} isLoading={movementsLoading} />
            </>
          ) : (
            <div className="pac-glass-raised rounded-lg px-4 py-8 text-center text-gray-500">
              Select a SKU from the Balances tab to view its movement history.
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── Internal stat badge ──────────────────────────────────────────────────────

function StatBadge({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="pac-glass-raised rounded-lg px-3 py-2 flex flex-col items-center min-w-[80px]">
      <span className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-lg font-semibold tabular-nums ${accent ?? 'text-white'}`}>{value}</span>
    </div>
  );
}
